import { createAsyncThunk } from "@reduxjs/toolkit";
import dayjs from "dayjs";

import { AuthorTypeEnum } from "@fitness-app/data-models/entities/AuthorTypeEnum";
import {
  MeasurementType,
  type BodyMeasurements,
  type WeightMeasurement,
} from "@fitness-app/data-models/entities/Measurement";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";
import { type Database } from "@fitness-app/supabase";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { type AsyncThunkCreator } from "../../../index";
import { TRAINEE_MEASUREMENTS_REDUCER_NAME } from "../types";

type Payload = {
  data: WeightMeasurement["data"] | BodyMeasurements["data"];
  eventDate: string;
  traineeId: string;
  id?: null | string;
  previousMeasurement?: WeightMeasurement | BodyMeasurements | null;
  userId?: string | null;
};

type MeasurementInsert = Database["public"]["Tables"]["measurement"]["Insert"];
type MeasurementUpdate = Database["public"]["Tables"]["measurement"]["Update"];

export const upsertMeasurement = createAsyncThunk<
  WeightMeasurement | BodyMeasurements,
  Payload,
  AsyncThunkCreator<string>
>(
  `${TRAINEE_MEASUREMENTS_REDUCER_NAME}/upsertMeasurement`,
  async (payload, { rejectWithValue, extra: { db, auth, analytics, errorTrackingService } }) => {
    const user = await getLoggedUser(auth);
    if (payload.previousMeasurement) {
      const { data, error } = await db
        .from("measurement")
        .update({
          data: payload.data,
          updatedAt: new Date().toISOString(),
        } as unknown as MeasurementUpdate)
        .eq("id", payload.previousMeasurement.id)
        .select("*")
        .single<WeightMeasurement | BodyMeasurements>();

      if (error) {
        errorTrackingService?.recordError(error, "upsertMeasurement", { errorMessage: error.message });
        return rejectWithValue(error.message);
      }

      return data;
    }

    const fragment =
      "weight" in payload.data
        ? { type: MeasurementType.WeightMeasurement, data: payload.data }
        : { type: MeasurementType.BodyMeasurements, data: payload.data };

    // A weight/body measurement is unique per (traineeId, type, eventDate). Update-first so a
    // duplicate add — dual actor (client in the mobile app + trainer in admin), a double-submit
    // race, or a stale client that never loaded the existing row via `previousMeasurement` —
    // converges onto that row instead of inserting a second one. The partial unique index
    // `measurement_daily_type_unique_idx` backstops the remaining insert race below.
    const updateExistingDay = () =>
      db
        .from("measurement")
        .update({ data: payload.data, updatedAt: new Date().toISOString() } as unknown as MeasurementUpdate)
        .match({ traineeId: payload.traineeId, type: fragment.type, eventDate: payload.eventDate })
        .select("*")
        .returns<(WeightMeasurement | BodyMeasurements)[]>();

    const { data: existing, error: existingError } = await updateExistingDay();

    if (existingError) {
      return rejectWithValue(existingError.message);
    }

    const [existingRow] = existing ?? [];
    if (existingRow) {
      analytics.track("add_measurement", { type: fragment.type });
      return existingRow;
    }

    const measurement = {
      ...fragment,
      eventDate: payload.eventDate,
      eventTimestamp: dayjs(payload.eventDate, "YYYY-MM-DD").hour(12).toISOString(),
      traineeId: payload.traineeId,
      creator: user.id === payload.userId || payload.userId === null ? AuthorTypeEnum.Client : AuthorTypeEnum.Trainer,
      id: generateUniqId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      userId: payload.userId || user.id,
    };

    const { error, data } = await db
      .from("measurement")
      .insert(measurement as unknown as MeasurementInsert)
      .select("*")
      .single<WeightMeasurement | BodyMeasurements>();

    if (error) {
      // Lost the insert race against a concurrent add: the row now exists, so update it instead
      // of surfacing the unique-violation from measurement_daily_type_unique_idx.
      if (error.code === "23505") {
        const { data: raced, error: racedError } = await updateExistingDay();
        const [racedRow] = raced ?? [];
        if (!racedError && racedRow) {
          analytics.track("add_measurement", { type: fragment.type });
          return racedRow;
        }
      }
      return rejectWithValue(error.message);
    }

    analytics.track("add_measurement", { type: measurement.type });

    return data;
  },
);
